import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { getDefaultMaxCapsuleDuration } from '@/services/systemConfiguration.utilities';
import { DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import {
  buildToolRehydrationState,
  getBasePersistedStateFromSnapshot,
  getNextToolParameterUnsetValue,
  getNextToolParameterValue,
  getRequiredToolParameterDefinition,
  getToolConfigParams,
  shouldHandleToolPayload,
  shouldResetToolStore,
  shouldSetToolName,
} from '@/tools/tool.utilities';
import type { PersistenceLevel } from '@/core/flux.service';
import type {
  BaseToolPersistedState,
  DurationWithUnits,
  ModelRehydratePayload,
  ParameterDefinitions,
  ToolAssetReference,
  ToolConditionDuration,
  ToolConditionMetadata,
  ToolConfigParams,
  ToolConfigValue,
  ToolDisplayModePayload,
  ToolItem,
  ToolParameter,
  ToolParameterDefinition,
  ToolParameterItemPayload,
  ToolRehydratePayload,
  ToolSetAdvancedParametersPayload,
  ToolSetMaximumDurationPayload,
  ToolSetNamePayload,
  ToolSignalMetadata,
} from '@/tools/tool.types';

/**
 * A base store that adds standard functionality for all stores that represent a tool used to create calculated series
 * or conditions.
 */
export type {
  BaseToolPersistedState,
  DurationWithUnits,
  ModelRehydratePayload,
  ParameterDefinitions,
  ToolAssetReference,
  ToolConditionDuration,
  ToolConditionMetadata,
  ToolConfigParams,
  ToolConfigValue,
  ToolDisplayModePayload,
  ToolItem,
  ToolParameter,
  ToolParameterDefinition,
  ToolParameterItemPayload,
  ToolRehydratePayload,
  ToolSetAdvancedParametersPayload,
  ToolSetMaximumDurationPayload,
  ToolSetNamePayload,
  ToolSignalMetadata,
};

export abstract class BaseToolStore extends Store {
  abstract type: string;
  parameterDefinitions: ParameterDefinitions = {};
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  readonly TOOL_ITEM_PROPS = DEFAULT_TOOL_ITEM_PROPS;

  /**
   * Used to rehydrate the tool in order to edit the calculated series.
   *
   * @param {Object} payload - An object with the necessary state to populate the edit form.
   * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
   * @param {Object[]} payload.parameters - The parameters used in the formula
   * @param {String} payload.formula - The formula for the calculated item
   */
  baseToolRehydrateForEdit = (payload: ToolRehydratePayload) => {
    if (shouldHandleToolPayload(this.type, payload.type)) {
      this.initialize();
      // The UIConfig is intentionally merged first because in pre-R18 versions some of the parameters were
      // stored as ids in the stores. For example `signalToAggregate` in histograms may be in UIConfig as an ID,
      // but we can safely overwrite that with the item from the parameters. CRAB-11230
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions: this.parameterDefinitions,
        currentAdvancedParametersCollapsed: this.state.get('advancedParametersCollapsed') as boolean | undefined,
        toolItemProps: this.TOOL_ITEM_PROPS,
      });
      this.state.merge(this.migrateSavedConfig(rehydrationState.config, rehydrationState.parameters, payload.formula));
      this.state.set('originalParameters', rehydrationState.originalParameters);
      _.forEach(rehydrationState.selectedParameters, (selected, name) => {
        this.state.set(name, selected);
      });
    }
  };

  /**
   * Resets all parameters if switching to new instance of the form. Also clears state when closing to ensure extra
   * state doesn't hang around and populate worksteps.
   *
   * @param {Object} payload - Object containing state information
   * @param {String} payload.mode - The display mode being set, one of DISPLAY_MODE
   * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
   */
  protected reset = (payload: ToolDisplayModePayload) => {
    if (shouldResetToolStore(this.type, payload)) {
      this.initialize();
    }
  };

  protected getBasePersistedState(): BaseToolPersistedState {
    return getBasePersistedStateFromSnapshot({
      id: this.state.get('id') as string | undefined,
      name: this.state.get('name') as string | undefined,
      originalParameters: this.state.get('originalParameters') as ToolItem[] | undefined,
      maximumDuration: this.state.get('maximumDuration') as DurationWithUnits | null | undefined,
      advancedParametersCollapsed: this.state.get('advancedParametersCollapsed') as boolean | undefined,
      configVersion: this.state.get('configVersion') as string | number | null | undefined,
    });
  }

  protected applyBasePersistedState(state: Partial<BaseToolPersistedState>) {
    if ('id' in state) {
      this.state.set('id', state.id);
    }
    if ('name' in state) {
      this.state.set('name', state.name);
    }
    if ('originalParameters' in state && state.originalParameters !== undefined) {
      this.state.set('originalParameters', state.originalParameters);
    }
    if ('maximumDuration' in state) {
      this.state.set('maximumDuration', state.maximumDuration ?? undefined);
    }
    if ('advancedParametersCollapsed' in state && state.advancedParametersCollapsed !== undefined) {
      this.state.set('advancedParametersCollapsed', state.advancedParametersCollapsed);
    }
    if ('configVersion' in state) {
      this.state.set('configVersion', state.configVersion);
    }
  }

  protected readonly handlers = {
    /**
     * Sets the name.
     *
     * @param {Object} payload - an Object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.name - The name of the calculated series
     * @param {String} payload.id - The id of the calculated series
     */
    TOOL_SET_SEARCH_NAME: (payload: ToolSetNamePayload) => {
      if (shouldSetToolName(this.type, this.state.get('id') as string | undefined, payload)) {
        this.state.set('name', payload.name);
        this.state.commit();
      }
    },

    /**
     * Sets the maximum length/duration of a capsule for tools like Value Search and Composite Search.
     *
     * @param {Object} payload - an Object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {Number} payload.value - The number that indicates how long the duration is
     * @param {String} payload.units - The units that the value represents
     */
    TOOL_SET_MAXIMUM_DURATION: (payload: ToolSetMaximumDurationPayload) => {
      if (shouldHandleToolPayload(this.type, payload.type)) {
        this.state.set('maximumDuration', {
          units: payload.units,
          value: payload.value,
        });
      }
    },

    /**
     * Sets the state of the expandable advanced parameters section.
     *
     * @param {Object} payload - an Object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.collapsed - True if the advanced section is collapsed.
     */
    TOOL_SET_ADVANCED_PARAMETERS_COLLAPSED_STATE: (payload: ToolSetAdvancedParametersPayload) => {
      if (shouldHandleToolPayload(this.type, payload.type)) {
        this.state.set('advancedParametersCollapsed', payload.collapsed);
      }
    },

    /**
     * Sets or adds, if multiple, one of the input parameters.
     *
     * @param {Object} payload - an object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.name - The name of the parameter. Must be defined as one of the parameterDefinitions
     * @param {Object} payload.item - The item to set
     */
    TOOL_SET_PARAMETER_ITEM: (payload: ToolParameterItemPayload) => {
      if (shouldHandleToolPayload(this.type, payload.type)) {
        const definition = getRequiredToolParameterDefinition(this.parameterDefinitions, payload.name);

        const nextValue = getNextToolParameterValue({
          definition,
          currentValue: this.state.get(payload.name) as ToolItem | ToolItem[] | null | undefined,
          item: payload.item,
          toolItemProps: this.TOOL_ITEM_PROPS,
        });

        if (!nextValue.changed) {
          return;
        }

        this.state.set(payload.name, nextValue.value);
      }
    },

    /**
     * Unsets or removes, if multiple, one of the input parameters.
     *
     * @param {Object} payload - an object representing state.
     * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
     * @param {String} payload.name - The name of the parameter. Must be defined as one of the parameterDefinitions
     * @param {Object} payload.item - The item to remove
     */
    TOOL_UNSET_PARAMETER_ITEM: (payload: ToolParameterItemPayload) => {
      if (shouldHandleToolPayload(this.type, payload.type)) {
        const definition = getRequiredToolParameterDefinition(this.parameterDefinitions, payload.name);

        const nextValue = getNextToolParameterUnsetValue({
          definition,
          currentValue: this.state.get(payload.name) as ToolItem | ToolItem[] | null | undefined,
          item: payload.item,
        });

        if (!nextValue.changed) {
          return;
        }

        this.state.set(payload.name, nextValue.value);
      }
    },

    TOOL_REHYDRATE_FOR_EDIT: this.baseToolRehydrateForEdit,

    INVESTIGATE_SET_DISPLAY_MODE: this.reset,
  };

  get baseHandlers(): typeof this.handlers {
    return this.handlers;
  }

  get id(): string {
    return this.state.get('id');
  }

  get name(): string {
    return this.state.get('name');
  }

  get originalParameters(): ToolItem[] {
    return this.state.get('originalParameters');
  }

  get signalMetadata(): ToolSignalMetadata | undefined {
    return this.state.get('signalMetadata');
  }

  get conditionMetadata(): ToolConditionMetadata | undefined {
    return this.state.get('conditionMetadata');
  }

  get advancedParametersCollapsed(): boolean {
    return this.state.get('advancedParametersCollapsed');
  }

  get configParams(): ToolConfigParams {
    return this.modifyConfigParams(
      getToolConfigParams({
        serializedState: this.state.serialize() as Record<string, ToolConfigValue>,
        toolType: this.type,
        parameterDefinitionKeys: Object.keys(this.parameterDefinitions),
        advancedParametersCollapsed: this.state.get('advancedParametersCollapsed') as boolean | undefined,
      }),
    );
  }

  get maximumDuration(): DurationWithUnits | undefined {
    return _.isUndefined(this.state.get('maximumDuration'))
      ? (getDefaultMaxCapsuleDuration() as DurationWithUnits)
      : this.state.get('maximumDuration');
  }

  /**
   * Provides a hook that can be overridden by the derived store. Previous versions of the state will be passed
   * into the migrateSavedConfig function. By default it returns the state unchanged.
   *
   * @param config - The UIConfig state to populate the form
   * @param parameters - The parameters used in the formula
   * @param formula - The formula for the calculated item
   */
  migrateSavedConfig<TConfig extends ToolConfigParams>(
    config: TConfig,
    _parameters: ToolParameter[],
    _formula?: string,
  ): TConfig {
    return config;
  }

  /**
   * Used to rehydrate the tool in order to edit the calculated series.
   *
   * @param {Object} payload - An object with the necessary state to populate the edit form.
   * @param {String} payload.type - The name of the tool, one of TREND_TOOLS
   * @param {Object[]} payload.parameters - The parameters used in the formula
   * @param {String} payload.formula - The formula for the calculated item
   */
  rehydrateForEdit(payload: ToolRehydratePayload) {
    this.baseToolRehydrateForEdit(payload);
  }

  /**
   * Provides a hook that can be overridden by the derived store. The configParams will be passed which is stored
   * in the UIConfig property and which can be modified by this function.
   *
   * @param {Object} config - The state that will be saved to UIConfig
   * @return {Object} The modified config
   */
  modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    return config;
  }

  /**
   * Small set of utility functions for building up formulas.
   */
  formulaBuilder = {
    /**
     * Create a variable reference.
     *
     * @param {String} name - The variable name
     * @param {String} [suffix] - Appended to the name if present
     * @returns {String} The variable name, e.g. $input
     */
    var(varName: string, suffix?: string): string {
      return `$${varName}${suffix || ''}`;
    },

    /**
     * Invoke an operator with arguments.
     *
     * @param operator - The name of the operator
     * @param args - The arguments to pass to the operator
     * returns The invocation of an operator, e.g. union($a, $b)
     */
    operator(operator: string, args: string[]): string {
      return `${operator}(${args.join(', ')})`;
    },

    /**
     * Formats a duration object as a string.
     *
     * @param {Object} duration - The duration
     * @param {String|Number} duration.value - The number that indicates how long the duration is
     * @param {String} duration.units - The units that the value represent
     * returns The formatted duration, e.g. 5min
     */
    duration(duration: { value: string | number; units: string }): string {
      return `${duration.value}${duration.units}`;
    },
  };
}
